/**
 * Permission Check Hook for Client Map
 * Verifies user has access to the client-map tool via Permission Service
 */
import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { SilentRequest } from "@azure/msal-browser";

// Permission Service API URL
const PERMISSION_API_URL = process.env.REACT_APP_PERMISSION_API_URL || "";

// Scopes for token acquisition
const TOKEN_SCOPES = ["openid", "profile", "email"];

interface PermissionCheckResult {
  hasAccess: boolean | null; // null = loading
  isLoading: boolean;
  error: string | null;
}

/**
 * Check if user has access to the client-map tool
 */
export function usePermissionCheck(): PermissionCheckResult {
  const { instance, accounts } = useMsal();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function checkPermission() {
      if (accounts.length === 0) {
        setIsLoading(false);
        return;
      }

      const account = accounts[0];
      const userId = account?.localAccountId || "";
      const userEmail = account?.username || "";
      const idTokenClaims = account?.idTokenClaims as Record<string, unknown> | undefined;
      const roles = (idTokenClaims?.roles as string[]) || [];

      // Portal.Admin has access to everything
      if (roles.includes("Portal.Admin")) {
        setHasAccess(true);
        setIsLoading(false);
        return;
      }

      // Portal.User only grants access to the portal shell, NOT individual tools
      // Must check Permission Service for explicit tool access

      // Check with Permission Service
      if (!PERMISSION_API_URL) {
        // If no Permission API configured, deny access (require explicit config)
        console.error("REACT_APP_PERMISSION_API_URL not configured - cannot verify permissions");
        setError("Permission service not configured. Please contact your administrator.");
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      // Acquire ID token via MSAL
      let idToken: string | null = null;
      try {
        const request: SilentRequest = {
          scopes: TOKEN_SCOPES,
          account: account,
        };
        const response = await instance.acquireTokenSilent(request);
        idToken = response.idToken;
      } catch (tokenError) {
        console.warn("Failed to acquire token silently:", tokenError);
      }

      try {
        const headers: HeadersInit = {
          "Content-Type": "application/json",
          // JWT Bearer token - primary authentication
          ...(idToken ? { "Authorization": `Bearer ${idToken}` } : {}),
          // Legacy headers for backwards compatibility
          "X-User-Id": userId,
          "X-User-Email": userEmail,
          "X-User-Roles": roles.join(","),
        };

        const response = await fetch(`${PERMISSION_API_URL}/permissions/user/me`, {
          headers,
        });

        if (!response.ok) {
          throw new Error(`Permission check failed: ${response.status}`);
        }

        const data = await response.json();
        
        // Check if user has explicit access to client-map tool
        // Portal.User does NOT grant tool access - must be explicitly granted
        const permissions = data.permissions || {};
        const hasClientMapAccess = permissions["client-map"] !== undefined;

        setHasAccess(hasClientMapAccess);
      } catch (err) {
        console.error("Permission check error:", err);
        setError("Unable to verify permissions. Please contact your administrator.");
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkPermission();
  }, [instance, accounts]);

  return { hasAccess, isLoading, error };
}

export default usePermissionCheck;
